import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Sparkles, Play, Building2, Bot, FileSearch, ScrollText,
  Gauge, FolderLock, GraduationCap, Store, ShieldCheck, TrendingUp,
} from "lucide-react";
import { PricingSection } from "../components/PricingSection";
import { DISCLAIMER } from "../constants";

const FEATURES = [
  { icon: Building2, title: "Business Credit Builder", desc: "Guided 21-step path from formation to funding readiness with live progress tracking.", span: "xl:col-span-2" },
  { icon: Bot, title: "AI Credit Coach", desc: "Ask anything about business or personal credit and get clear, educational guidance." },
  { icon: FileSearch, title: "AI Report Analyzer", desc: "Upload a report and get a plain-English summary with a prioritized action plan." },
  { icon: ScrollText, title: "AI Dispute Assistant", desc: "Generate professional dispute letter templates with document checklists." },
  { icon: Gauge, title: "Funding Readiness", desc: "Educational readiness score across foundation, credit, banking & documentation.", span: "xl:col-span-2" },
  { icon: Store, title: "Net-30 & Vendor Center", desc: "Track vendors, applications, reporting status and payment dates." },
  { icon: FolderLock, title: "Document Vault", desc: "Securely store formation docs, EIN letters, licenses, and more." },
  { icon: GraduationCap, title: "Learning Center", desc: "Searchable, plain-English education on every credit-building topic." },
];

const Header = () => (
  <header className="fixed top-0 inset-x-0 z-50 cc-glass border-b border-white/[0.06]">
    <div className="max-w-7xl mx-auto flex items-center justify-between h-16 px-5 sm:px-8">
      <Link to="/credit" className="flex items-center gap-2.5">
        <div className="flex h-9 w-9 items-center justify-center rounded-xl cc-gradient-br cc-glow-blue">
          <Sparkles size={18} className="text-white" />
        </div>
        <span className="cc-heading font-600 text-white">ArcaCentum <span className="cc-gradient-text">Credit</span></span>
      </Link>
      <div className="hidden md:flex items-center gap-7 text-sm text-slate-300">
        <a href="#features" className="hover:text-white cc-nav-item px-1">Features</a>
        <a href="#pricing" className="hover:text-white cc-nav-item px-1">Pricing</a>
        <Link to="/" className="hover:text-white cc-nav-item px-1">ArcaCentum.com</Link>
      </div>
      <div className="flex items-center gap-2.5">
        <Link to="/credit/login" data-testid="landing-login" className="text-sm text-slate-300 hover:text-white px-3 py-2">Log in</Link>
        <span data-testid="landing-signup" className="inline-flex items-center gap-2 rounded-full border border-amber-300/25 bg-amber-400/10 px-4 py-2 text-xs font-500 uppercase tracking-[0.14em] text-amber-200">
          Coming Soon
        </span>
      </div>
    </div>
  </header>
);

export default function CreditLanding() {
  return (
    <div className="cc cc-bg min-h-screen text-slate-200">
      <Header />

      {/* Hero */}
      <section className="relative pt-36 pb-24 px-5 sm:px-8 overflow-hidden">
        <div className="pointer-events-none absolute inset-0">
          <img src="https://images.unsplash.com/photo-1617791160505-6f00504e3519" alt="" aria-hidden className="w-full h-full object-cover opacity-25" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0a0a0f]/70 via-[#0a0a0f]/85 to-[#0a0a0f]" />
        </div>
        <div className="relative max-w-4xl mx-auto text-center">
          <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
            className="inline-flex items-center gap-2 rounded-full border border-white/10 cc-surface px-4 py-1.5 text-xs text-slate-300 mb-4">
            <span className="h-1.5 w-1.5 rounded-full bg-blue-400" /> Part of the ArcaCentum ecosystem
          </motion.div>
          <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.05 }}
            data-testid="credit-dev-badge"
            className="block mb-7">
            <span className="inline-flex items-center gap-2 rounded-full border border-amber-300/25 bg-amber-400/10 px-4 py-1.5 text-[11px] uppercase tracking-[0.18em] text-amber-200">
              <span className="h-1.5 w-1.5 rounded-full bg-amber-300 animate-pulse" /> In Development · Coming Soon
            </span>
          </motion.div>
          <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.1 }}
            className="cc-heading text-5xl sm:text-6xl font-700 tracking-tighter leading-[1.02] text-white">
            Build Better Credit.
            <br />
            <span className="cc-gradient-text">Build Bigger Opportunities.</span>
          </motion.h1>
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.2 }}
            className="mt-6 text-lg text-slate-400 max-w-2xl mx-auto">
            Build stronger business and personal credit using AI-powered guidance, intelligent planning tools,
            funding readiness assessments, and professional credit management.
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.3 }}
            className="mt-9 flex flex-col sm:flex-row items-center justify-center gap-3">
            <span data-testid="hero-start-free" className="inline-flex items-center gap-2 rounded-full border border-amber-300/25 bg-amber-400/10 px-7 py-3.5 text-sm font-500 uppercase tracking-[0.12em] text-amber-200">
              Coming Soon
            </span>
            <a href="#features" data-testid="hero-explore" className="inline-flex items-center gap-2 rounded-full border border-white/12 px-7 py-3.5 text-sm text-white cc-nav-item">
              Explore Features
            </a>
            <a href="#demo" data-testid="hero-demo" className="inline-flex items-center gap-2 rounded-full px-5 py-3.5 text-sm text-slate-300 hover:text-white">
              <Play size={15} className="text-blue-300" /> Watch Demo
            </a>
          </motion.div>
        </div>
      </section>

      {/* Trust bar */}
      <section className="px-5 sm:px-8 pb-6">
        <div className="max-w-5xl mx-auto grid grid-cols-2 sm:grid-cols-4 gap-4">
          {[
            { icon: TrendingUp, label: "Foundation to Funding" },
            { icon: Bot, label: "AI-Powered Guidance" },
            { icon: ShieldCheck, label: "Secure Document Vault" },
            { icon: GraduationCap, label: "Education First" },
          ].map((s) => (
            <div key={s.label} className="cc-card rounded-xl px-4 py-4 flex items-center gap-3">
              <s.icon size={18} className="text-blue-300" />
              <span className="text-sm text-slate-300">{s.label}</span>
            </div>
          ))}
        </div>
      </section>

      {/* Features */}
      <section id="features" className="px-5 sm:px-8 py-24 max-w-7xl mx-auto">
        <div className="text-center mb-14">
          <span className="text-xs tracking-[0.2em] uppercase text-slate-500">Everything you need</span>
          <h2 className="cc-heading text-3xl sm:text-4xl font-600 tracking-tight text-white mt-3">
            The TurboTax + Notion + ChatGPT of credit
          </h2>
        </div>
        <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-5">
          {FEATURES.map((f, i) => (
            <motion.div key={f.title}
              initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.05 }}
              className={`cc-card rounded-2xl p-6 ${f.span || ""}`}>
              <div className="inline-flex h-11 w-11 items-center justify-center rounded-xl cc-surface-2 text-blue-300 mb-4">
                <f.icon size={20} strokeWidth={1.7} />
              </div>
              <h3 className="cc-heading text-lg font-500 text-white">{f.title}</h3>
              <p className="text-slate-400 text-sm mt-2 leading-relaxed">{f.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Demo placeholder */}
      <section id="demo" className="px-5 sm:px-8 pb-24 max-w-5xl mx-auto">
        <div className="cc-card rounded-3xl p-2 overflow-hidden">
          <div className="relative rounded-2xl overflow-hidden aspect-video bg-[#0c0c14] flex items-center justify-center">
            <img src="https://images.unsplash.com/photo-1503945438517-f65904a52ce6" alt="Product demo preview" className="absolute inset-0 w-full h-full object-cover opacity-30" />
            <div className="relative flex flex-col items-center">
              <div className="flex h-16 w-16 items-center justify-center rounded-full cc-gradient cc-glow-blue">
                <Play size={26} className="text-white ml-1" />
              </div>
              <span className="mt-4 text-sm text-slate-300">Product walkthrough</span>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="px-5 sm:px-8 py-20 max-w-7xl mx-auto">
        <div className="text-center mb-12">
          <span className="text-xs tracking-[0.2em] uppercase text-slate-500">Pricing</span>
          <h2 className="cc-heading text-3xl sm:text-4xl font-600 tracking-tight text-white mt-3">Simple, transparent plans</h2>
          <p className="text-slate-400 mt-3">Start free. Upgrade when you're ready. Cancel anytime.</p>
        </div>
        <PricingSection compact />
      </section>

      {/* CTA */}
      <section className="px-5 sm:px-8 pb-24 max-w-5xl mx-auto">
        <div className="cc-card rounded-3xl p-10 sm:p-14 text-center relative overflow-hidden">
          <div className="pointer-events-none absolute -top-16 left-1/2 -translate-x-1/2 h-56 w-56 rounded-full bg-blue-600/20 blur-[80px]" />
          <h2 className="relative cc-heading text-3xl sm:text-4xl font-600 text-white">Start building today</h2>
          <p className="relative text-slate-400 mt-3 max-w-xl mx-auto">
            Join entrepreneurs organizing and understanding their business and personal credit with ArcaCentum Credit.
          </p>
          <span data-testid="bottom-cta-coming-soon" className="relative inline-flex items-center gap-2 rounded-full border border-amber-300/25 bg-amber-400/10 px-8 py-4 text-sm font-500 uppercase tracking-[0.12em] text-amber-200 mt-8">
            Coming Soon
          </span>
        </div>
      </section>

      <footer className="border-t border-white/[0.06] px-5 sm:px-8 py-10">
        <div className="max-w-7xl mx-auto">
          <p className="text-xs text-slate-500 leading-relaxed max-w-3xl">{DISCLAIMER}</p>
          <div className="mt-6 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-slate-500">
            <span>© {new Date().getFullYear()} ArcaCentum, Inc. · ArcaCentum Credit is an ArcaCentum product</span>
            <div className="flex flex-wrap justify-center gap-x-5 gap-y-2">
              <Link to="/" className="hover:text-slate-300">ArcaCentum.com</Link>
              <Link to="/credit/login" className="hover:text-slate-300">Log in</Link>
              <a href="#pricing" className="hover:text-slate-300">Pricing</a>
              <Link to="/help" data-testid="credit-footer-help" className="hover:text-slate-300">Help Center</Link>
              <Link to="/privacy" data-testid="credit-footer-privacy" className="hover:text-slate-300">Privacy Policy</Link>
              <Link to="/terms" data-testid="credit-footer-terms" className="hover:text-slate-300">Terms of Service</Link>
              <Link to="/refund-policy" data-testid="credit-footer-refunds" className="hover:text-slate-300">Refund Policy</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
